import * as qs from 'qs';

import { reportTypeDefaultFormatMap, reportTypeFormatMap } from '../shared/constants';
import { buildFixPlanParams, buildIncludeParams, buildExcludeParams } from '../shared/utils';
import { ReportCategories } from '../utils';
import type { FixPlansParams } from '../../../../../../shared/types/routes/fixPlans';
import type { ReportCategory, ReportExclusionOptions, ReportURLParams } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption, IncludeFixPlanFilterOption } from '../shared/types';

const getEndpoint = ({
  groupId,
  releaseId,
  reportType,
  format,
  query,
  includeOptions,
  dependencyMetadataOptions,
  excludeOptions,
  authorName,
  authorEmail,
  supplier,
}: {
  groupId: number;
  releaseId: number;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  excludeOptions?: ReportExclusionOptions;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  query: {
    preview?: boolean;
    download?: boolean;
    isPublishing?: boolean;
    emails?: string;
  };
}) => {
  const isVulnerability = reportType === ReportCategories.Vulnerability;
  const defaultFileFormat = isVulnerability ? 'security' : reportTypeDefaultFormatMap[reportType];
  const formatQuery = format ? reportTypeFormatMap[format] : defaultFileFormat;
  const urlParams: ReportURLParams = {
    ...query,
    ...buildIncludeParams(includeOptions),
    ...(excludeOptions ? buildExcludeParams(excludeOptions) : {}),
    dependencyInfoOptions: dependencyMetadataOptions,
    authorName,
    authorEmail,
    supplier,
  };
  // Security reports do not have a format query parameter. The response is always in PDF format.
  if (formatQuery !== 'security') {
    urlParams.format = formatQuery;
  }
  const queryString = qs.stringify(urlParams);

  return `/api/project_group/${groupId}/release/${releaseId}/attribution/${formatQuery}?${queryString}`;
};

const getFixPlansEndpoint = ({
  groupId,
  releaseId,
  format,
  query,
}: {
  groupId: number;
  releaseId: number;
  format: FormatName | null;
  query: FixPlansParams;
}) => {
  const queryString = qs.stringify({
    ...query,
    format: format ? reportTypeFormatMap[format] : reportTypeDefaultFormatMap[ReportCategories.RemediationGuidance],
  });

  return `/api/project_group/${groupId}/release/${releaseId}/report/remediation-guidance?${queryString}`;
};

/**
 * Get the preview URL for a report at the release group level
 */
export const getPreviewURL = ({
  groupId,
  releaseId,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  fixPlanOptions,
  excludeOptions,
  authorName,
  authorEmail,
  supplier,
}: {
  groupId: number;
  releaseId: number;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  fixPlanOptions: IncludeFixPlanFilterOption[];
  excludeOptions?: ReportExclusionOptions;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
}) => {
  if (reportType === ReportCategories.RemediationGuidance) {
    return getFixPlansEndpoint({
      groupId,
      releaseId,
      format,
      query: { ...buildFixPlanParams(fixPlanOptions), preview: true },
    });
  }
  return getEndpoint({
    groupId,
    releaseId,
    format,
    includeOptions,
    dependencyMetadataOptions,
    excludeOptions,
    query: {
      preview: true,
    },
    reportType,
    authorName,
    authorEmail,
    supplier,
  });
};

/**
 * Get the download URL for a report at the release group level
 */
export const getEmailURL = ({
  groupId,
  releaseId,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  excludeOptions,
  emails,
  authorName,
  authorEmail,
  supplier,
}: {
  groupId: number;
  releaseId: number;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  excludeOptions?: ReportExclusionOptions;
  emails: string;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
}) => {
  return getEndpoint({
    groupId,
    releaseId,
    format,
    includeOptions,
    dependencyMetadataOptions,
    excludeOptions,
    query: {
      emails,
    },
    reportType,
    authorName,
    authorEmail,
    supplier,
  });
};

/**
 * Get the URL to publish a report to the SBOM portal.
 */
export const getPublishURL = ({
  groupId,
  releaseId,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  excludeOptions,
  authorName,
  authorEmail,
  supplier,
}: {
  groupId: number;
  releaseId: number;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  excludeOptions?: ReportExclusionOptions;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
}) => {
  return getEndpoint({
    groupId,
    releaseId,
    format,
    includeOptions,
    dependencyMetadataOptions,
    excludeOptions,
    query: {
      isPublishing: true,
    },
    reportType,
    authorName,
    authorEmail,
    supplier,
  });
};

/**
 * Get the download URL for a report at the release group level.
 * At this moment, the download feature is only available for vulnerability reports at the release group level.
 */
export const getDownloadVulnerabilityReportURL = ({
  groupId,
  releaseId,
  includeOptions,
  dependencyMetadataOptions,
  excludeOptions,
}: {
  groupId: number;
  releaseId: number;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  excludeOptions?: ReportExclusionOptions;
}) => {
  return getEndpoint({
    groupId,
    releaseId,
    includeOptions,
    dependencyMetadataOptions,
    excludeOptions,
    format: null, // Vulnerability reports format is not configurable
    query: {
      download: true,
    },
    reportType: 'vulnerability',
  });
};

/**
 * Get the download URL for a report at the release group level.
 * At this moment, the download feature is only available for vulnerability reports at the release group level.
 */
export const getDownloadFixPlansReportURL = ({
  groupId,
  releaseId,
  format,
  fixPlanOptions,
}: {
  groupId: number;
  releaseId: number;
  format: FormatName | null;
  fixPlanOptions: IncludeFixPlanFilterOption[],
}) => {
  return getFixPlansEndpoint({
    groupId,
    releaseId,
    format,
    query: { ...buildFixPlanParams(fixPlanOptions), bundle: true },
  });
};

/**
 * Get the SBOM portal type based on the format of the report.
 */
export const getSBOMPortalReportType = (
  reportType: ReportCategory,
  format: FormatName | null,
): 'SBOM' | 'Attribution' | null => {
  if (reportType === 'sbom') {
    return 'SBOM';
  }
  if (reportType === 'licensing' && ['Plain Text'].includes(format || '')) {
    return 'Attribution';
  }

  return null;
};
